'use client';

import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cn } from '@packages/shared/src/utils/cn/cn';
import {
  ColorButton,
  type ColorButtonProps,
} from '@packages/shared/src/ui/ColorButton/ColorButton';

// TODO calculate this based on the size of the color buttons, to support not just 2 and 3 colors, but any number as long as the space allows
const MIN_WIDTH_FOR_THREE_COLORS = 160;

const messages = defineMessages({
  colorsHidden: {
    id: 'product-colors.colors-hidden-label',
    defaultMessage: '+{colorsHidden} {colorsHidden, plural, one {Farbe} other {Farben}}',
  },
});

interface ProductCardV2ColorSwitcherProps {
  /** Array of color options */
  colors: readonly ColorButtonProps[];
  /** Callback when a color is selected */
  onColorChange?: (index: number) => void;
  /** Additional classes */
  className?: string;
  /** Currently selected color */
  activeIndex: number;
  /** Loading state if data fetching is necessary to update colors */
  isLoading?: boolean;
}

export const ProductCardV2ColorSwitcher = ({
  colors,
  onColorChange,
  className,
  activeIndex,
  isLoading,
}: ProductCardV2ColorSwitcherProps) => {
  const [visibleColorsCount, setVisibleColorsCount] = useState(3);

  const containerRef = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();

  useEffect(() => {
    const element = containerRef?.current;
    if (!element) return undefined;

    const observer = new ResizeObserver(() => {
      setVisibleColorsCount(element.clientWidth >= MIN_WIDTH_FOR_THREE_COLORS ? 3 : 2);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (!colors?.length) return null;

  const visibleColors = colors.slice(0, visibleColorsCount);
  const hiddenColorsCount = colors.length - visibleColors.length;

  const handleColorChange = (name: ColorButtonProps['name'], index: number) => {
    onColorChange?.(index);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'product-card-color-switcher pointer-events-none relative z-20 mx-2 mt-1 flex items-center gap-1 [grid-area:9/2/span_1/span_1]',
        className,
      )}
      role="radiogroup"
    >
      {visibleColors.map((colorProps, index) => (
        <ColorButton
          key={colorProps.name}
          {...colorProps}
          minimal
          rounded
          active={activeIndex === index && !isLoading}
          isSelected={activeIndex === index}
          aria-atomic="true"
          role="radio"
          aria-checked={activeIndex === index}
          onClick={() => handleColorChange(colorProps.name, index)}
          className="pointer-events-auto"
        />
      ))}
      {hiddenColorsCount > 0 && visibleColorsCount > 0 && (
        <span className="text-size-body-xs">
          {formatMessage(messages.colorsHidden, { colorsHidden: hiddenColorsCount })}
        </span>
      )}
    </div>
  );
};
